/**
 * Global Cache Configuration
 * Provides unified cache TTL settings and interfaces for all providers
 */

// Global cache TTL aligned with auth token expiry (45 minutes < 1 hour token expiry)
// This prevents auth/cache mismatches across all providers
export const GLOBAL_CACHE_TTL_MS = 45 * 60 * 1000; // 45 minutes

/**
 * Generic cache data interface
 * All providers should use this structure for timestamped cache data
 */
export interface CacheData<T> {
  timestamp: number;
  data: T;
}

/**
 * Provider-specific cache configuration interface
 * Each provider implements this to define their cache keys and behavior
 */
export interface ProviderCacheConfig {
  name: string;
  keys: Record<string, string>;
  ttl?: number; // Override global TTL if needed
}

/**
 * Utility to create timestamped cache data
 * @param data - The data to cache
 * @returns Timestamped cache object
 */
export function createCacheData<T>(data: T): CacheData<T> {
  return {
    timestamp: new Date().getTime(),
    data
  };
}

/**
 * Utility to check if cached data is still fresh
 * @param cacheData - The cached data object
 * @param ttl - Time-to-live in milliseconds (defaults to global TTL)
 * @returns Boolean indicating if cache is fresh
 */
export function isCacheFresh<T>(cacheData: CacheData<T>, ttl: number = GLOBAL_CACHE_TTL_MS): boolean {
  const currentTime = new Date().getTime();
  const cacheAge = currentTime - cacheData.timestamp;
  return cacheAge <= ttl;
}

/**
 * Utility to get cache age in human-readable format
 * @param cacheData - The cached data object
 * @returns Cache age in seconds as string
 */
export function getCacheAge<T>(cacheData: CacheData<T>): string {
  const currentTime = new Date().getTime();
  const cacheAge = currentTime - cacheData.timestamp;
  return `${Math.round(cacheAge / 1000)}s`;
}